/**
 * Post-settle confirmation for the bulk market-data on/off actions, shared by
 * the opener and unwinder tabs. `waitForLiveSettle` is a UX cap, not a
 * confirmation — when the WS echo never lands within the cap, the spinner
 * releases with the switches still on their old value. The per-row toggle
 * already warns in that case; these helpers give the bulk paths the same
 * honesty: aggregate the server's `{matched, modified}` counters per chunk,
 * then re-check the live snapshots after the settle and surface the ids the
 * echo never confirmed.
 */

export interface BulkWriteCounts {
  matched: number;
  modified: number;
}

/** Sum the per-chunk `{matched, modified}` envelopes into one total. */
export function aggregateBulkCounts(
  results: ReadonlyArray<{ matched?: number; modified?: number }>
): BulkWriteCounts {
  return results.reduce<BulkWriteCounts>(
    (acc, r) => ({
      matched: acc.matched + (r.matched ?? 0),
      modified: acc.modified + (r.modified ?? 0)
    }),
    { matched: 0, modified: 0 }
  );
}

/** Chunks in flight at once for the chunked bulk on/off requests. */
export const BULK_ENABLE_CONCURRENCY = 3;

/**
 * Thrown by `runWithConcurrency` when any chunk of a group rejects. Raised
 * only AFTER that group fully settles, so no write is still in flight when
 * the caller sees it: everything that landed server-side — earlier groups
 * plus the failing group's surviving siblings — is in `partialResults` (in
 * input order), and `cause` is the first rejection reason. Callers use
 * `partialResults` to keep the settle + unconfirmed re-check honest about
 * the pairs the surviving chunks flipped.
 */
export class BulkPartialFailureError extends Error {
  readonly partialResults: unknown[];
  readonly cause: unknown;

  constructor(partialResults: unknown[], cause: unknown) {
    super(
      cause instanceof Error
        ? `Bulk chunk failed: ${cause.message}`
        : 'Bulk chunk failed'
    );
    this.name = 'BulkPartialFailureError';
    this.partialResults = partialResults;
    this.cause = cause;
  }
}

/**
 * Run `fn` over `items` with bounded concurrency: `limit` at a time via
 * `Promise.allSettled` over groups. Results keep the input order. A rejection
 * in a group throws `BulkPartialFailureError` AFTER that group settles —
 * later groups are never started and no write is still in flight when the
 * error surfaces; every fulfilled result (earlier groups + the failing
 * group's surviving siblings) rides on the error's `partialResults`.
 */
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += limit) {
    const settled = await Promise.allSettled(items.slice(i, i + limit).map(fn));
    for (const s of settled) {
      if (s.status === 'fulfilled') out.push(s.value);
    }
    const rejected = settled.find(
      (s): s is PromiseRejectedResult => s.status === 'rejected'
    );
    if (rejected) throw new BulkPartialFailureError(out, rejected.reason);
  }
  return out;
}

/**
 * Ids whose live snapshot does NOT satisfy the settle predicate — the same
 * predicate handed to `waitForLiveSettle`, re-applied after the wait to tell
 * "settled" apart from "gave up at the cap".
 */
export function collectUnconfirmedIds<T>(
  store: { getKeySnapshot(id: string): T | undefined },
  ids: readonly string[],
  settled: (state: T | undefined) => boolean
): string[] {
  return ids.filter(id => !settled(store.getKeySnapshot(id)));
}

/**
 * Warning copy for a bulk on/off whose WS echo did not confirm every targeted
 * pair within the settle cap. Quotes the server's aggregated counters so the
 * operator can tell a slow echo (modified matches the target) from a write
 * that genuinely changed fewer pairs.
 */
export function unconfirmedBulkMessage(
  unconfirmedCount: number,
  targetedCount: number,
  counts: BulkWriteCounts
): string {
  return (
    `${unconfirmedCount} of ${targetedCount} pairs not confirmed yet ` +
    `(server matched ${counts.matched}, modified ${counts.modified}) — ` +
    'verify the switches reflect the real state.'
  );
}
